import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { AutoSizer, VirtualTable } from "@snapchat/snap-design-system";
import { useLoadLensesV2 } from "../containers/useLoadLensesV2";
import { NoResults } from "../../groups/components/NoResults";
import { useTableFilterAndSort } from "../../common/hooks/useTableFilterAndSort";
import { SearchableCard } from "../../common/components/SearchableCard";
import { LensListItemModel } from "../models";
import { getColumns, type LensTableItem } from "./AddLensesTableColumns";
import { searchItemsContainer } from "./AddLensesModal.styles";

const emptySet = new Set();

type ExtendedLensTableItem = LensTableItem & Record<string, unknown>;

interface AddLensesTableProps {
    selectedLenses: ReadonlyMap<string, LensListItemModel>;
    onToggleSelection: (lens: LensListItemModel) => void;
    alreadySelectedIds?: Set<string>;
}

export const AddLensesTable: FC<AddLensesTableProps> = ({
    onToggleSelection,
    selectedLenses,
    alreadySelectedIds = emptySet,
}) => {
    const { lenses, loadMore, isLastPage, loading } = useLoadLensesV2();
    const loadMoreData = useCallback(async () => loadMore(), [loadMore]);

    const [searchText, setSearchText] = useState("");

    // TODO: remove when working on https://jira.sc-corp.net/browse/CAMKIT-681
    const onSearch = useCallback((text: string) => setSearchText(text), [setSearchText]);

    const columns = useMemo(() => getColumns({ onToggleSelection }), [onToggleSelection]);
    const data = useMemo(
        () =>
            lenses.map((lens) => ({
                lens,
                selected: selectedLenses.has(lens.compoundId.id as string),
                alreadySelected: alreadySelectedIds.has(lens.compoundId.id),
            })) as readonly LensTableItem[],
        [lenses, selectedLenses, alreadySelectedIds],
    );

    const getSearchKey = useCallback(
        ({ lens }: ExtendedLensTableItem) => `${lens.name} ${lens.compoundId.id} ${lens.lensSourceInfo.name}`,
        [],
    );

    const { data: filteredData } = useTableFilterAndSort({
        tableData: data as ExtendedLensTableItem[],
        searchText,
        getSearchKey,
    });

    useEffect(() => {
        if (!isLastPage && !loading) {
            loadMore();
        }
    }, [isLastPage, loading, loadMore]);

    return (
        <SearchableCard
            padBody={false}
            isLoading={!isLastPage}
            searchPlaceholder="Search by Lens ID, Name, or Source"
            className={searchItemsContainer}
            onSearch={onSearch}
            searchValue={searchText}
            tooltip="Lenses are still loading."
        >
            <AutoSizer disableHeight>
                {({ width }: { width: number }) => (
                    <VirtualTable
                        isLoading={loading && data.length === 0}
                        height={496}
                        width={width}
                        rowHeight={56}
                        columns={columns}
                        data={filteredData as ExtendedLensTableItem[]}
                        loadMore={loadMoreData}
                        hasMore={!isLastPage}
                        emptyMessage={<NoResults description="No Lenses found." />}
                    />
                )}
            </AutoSizer>
        </SearchableCard>
    );
};
